import { DEFAULT_EUL_TYPE, isEulType, type EulType } from '../constants/eul-types';

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const EUL_FAILURE_RATE_MAP: Record<Exclude<EulType, 'UNKNOWN'>, number> = {
  B10: 0.1,
  B20: 0.2,
  B30: 0.3,
  B50: 0.5,
};

export const calculateLifespanBySelectedEulType = (
  b632LifeYears: number | null,
  eulType: EulType,
  beta: number | null,
): number | null => {
  if (b632LifeYears === null || !Number.isFinite(b632LifeYears) || b632LifeYears <= 0) {
    return null;
  }

  const normalizedEulType: EulType = isEulType(eulType) ? eulType : DEFAULT_EUL_TYPE;
  if (normalizedEulType === 'UNKNOWN') {
    return roundToTwoDecimals(b632LifeYears);
  }

  const failureRate = EUL_FAILURE_RATE_MAP[normalizedEulType];
  if (!failureRate) {
    return null;
  }

  const normalizedBeta = beta && Number.isFinite(beta) && beta > 0 ? beta : 2;
  const conversionCoefficient = Math.pow(1 / (-Math.log(1 - failureRate)), 1 / normalizedBeta);
  if (!Number.isFinite(conversionCoefficient) || conversionCoefficient <= 0) {
    return null;
  }

  return roundToTwoDecimals(b632LifeYears / conversionCoefficient);
};

const normalizeFactor = (factor: number | null | undefined): number => {
  if (typeof factor === 'number' && Number.isFinite(factor) && factor > 0) {
    return factor;
  }
  return 1;
};

export const calculateEnvironmentAdjustedLifespan = (
  baseLifespanYears: number | null,
  environmentalFactor: number | null | undefined,
  maintenanceFactor: number | null | undefined,
): number | null => {
  if (baseLifespanYears === null || !Number.isFinite(baseLifespanYears) || baseLifespanYears <= 0) {
    return null;
  }

  const adjustedLifespanYears =
    baseLifespanYears * normalizeFactor(environmentalFactor) * normalizeFactor(maintenanceFactor);

  if (!Number.isFinite(adjustedLifespanYears) || adjustedLifespanYears <= 0) {
    return null;
  }

  return roundToTwoDecimals(adjustedLifespanYears);
};
